import React, { createContext, useContext, useEffect, useState } from 'react';

export interface AppSettings {
  githubUser: string;
  consciousness: string;
  neuralpathway: string;
  basePath: string;
  enableAudio: boolean;
  disableMarkdown: boolean;
  debug: boolean;
}

export interface CosmosRepo {
  repo: string;
  label: string;
  default_branch?: string;
  base_path?: string;
}

export interface CosmosContributor {
  github: string;
  display: string;
  repos: CosmosRepo[];
}

export interface CosmosLogos {
  default_branch: string;
  contributors: CosmosContributor[];
}

const getEnvOr = (key: string, fallback: string): string =>
  process.env[`REACT_APP_${key}`] || fallback;

// ✅ Grab settings from URL parameters regardless of debug mode
const getRuntimeOverrides = (): Partial<AppSettings> => {
  const params = new URLSearchParams(window.location.search);
  const runtime: Partial<AppSettings> = {};

  if (params.has('user')) runtime.githubUser = params.get('user')!;
  if (params.has('consciousness')) runtime.consciousness = params.get('consciousness')!;
  if (params.has('neuralpathway')) runtime.neuralpathway = params.get('neuralpathway')!;
  if (params.has('base_path')) runtime.basePath = params.get('base_path')!;
  if (params.get('audio') === 'true') runtime.enableAudio = true;
  if (params.get('audio') === 'false') runtime.enableAudio = false;
  if (params.get('disableMarkdown') === 'true') runtime.disableMarkdown = true;
  if (params.get('disableMarkdown') === 'false') runtime.disableMarkdown = false;
  if (params.get('debug') === 'true') runtime.debug = true;

  return runtime;
};

const defaultSettings: AppSettings = {
  githubUser: getEnvOr('GITHUB_USER', 'alchemisthomer'),
  consciousness: getEnvOr('REPO_NAME', 'odyssey-press'),
  neuralpathway: getEnvOr('BRANCH', 'main'),
  basePath: getEnvOr('BASE_PATH', 'source_of_truth'),
  enableAudio: false,
  disableMarkdown: false,
  debug: false,
};

const AppSettingsContext = createContext<{
  settings: AppSettings;
  setSettings: (settings: AppSettings) => void;
  cosmosLogos: CosmosLogos | null;
}>({
  settings: defaultSettings,
  setSettings: () => {},
  cosmosLogos: null,
});

export const AppSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const params = new URLSearchParams(window.location.search);
  const shouldReset = params.get('reset') === '1';

  if (shouldReset) {
    localStorage.removeItem('odyssey_settings');
  }

  const [cosmosLogos, setCosmosLogos] = useState<CosmosLogos | null>(null);

  const [settings, setSettingsState] = useState<AppSettings>(() => {
    const stored = localStorage.getItem('odyssey_settings');
    const saved = stored && !shouldReset ? JSON.parse(stored) : {};
    const runtime = getRuntimeOverrides();

    return {
      ...defaultSettings,
      ...saved,
      ...Object.fromEntries(Object.entries(runtime).filter(([_, v]) => v !== undefined)),
    };
  });

  useEffect(() => {
    const fetchCosmosLogos = async () => {
      const url = [
        'https://raw.githubusercontent.com',
        settings.githubUser,
        settings.consciousness,
        settings.neuralpathway,
        settings.basePath,
        'cosmos-logos.json'
      ]
        .map(p => p.replace(/^\/|\/$/g, ''))
        .join('/');

      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        setCosmosLogos(data);
      } catch (err) {
        console.warn('⚠️ Failed to load cosmos-logos.json', err);
      }
    };

    fetchCosmosLogos();
  }, [settings.githubUser, settings.consciousness, settings.neuralpathway, settings.basePath]);

  const setSettings = (newSettings: AppSettings) => {
    setSettingsState(newSettings);
    localStorage.setItem('odyssey_settings', JSON.stringify(newSettings));
  };

  return (
    <AppSettingsContext.Provider value={{ settings, setSettings, cosmosLogos }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => useContext(AppSettingsContext);
